import { useState } from "react";
import { MapPin, CheckCircle, Spinner } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import API from "../lib/api";

/**
 * "Mark as Arrived" button for suppliers.
 *
 * On click:
 *   1. Attempts to capture browser geolocation (best-effort, works out-of-the-box on mobile)
 *   2. POSTs to /supplier-portal/tasks/{id}/arrived  or  /supplier-portal/work-orders/{id}/arrived
 *   3. Calls onArrived(updatedRow) so parent can update its UI
 *
 * Props:
 *   type        - "task" | "wo"
 *   id          - task or WO id
 *   arrivedAt   - ISO string if already arrived (renders the "Arrived" chip instead of button)
 *   onArrived   - optional callback({ arrived_at, travel_time_seconds })
 *   size        - button size (default: "sm")
 *   variant     - button variant (default: "default")
 *   compact     - compact mode (smaller chip/button)
 */
export default function MarkAsArrivedButton({
  type,
  id,
  arrivedAt,
  onArrived,
  size = "sm",
  variant = "default",
  compact = false,
  className = "",
  "data-testid": testId,
}) {
  const [loading, setLoading] = useState(false);
  const [localArrivedAt, setLocalArrivedAt] = useState(arrivedAt || null);

  const formatTime = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  if (localArrivedAt) {
    return (
      <div
        className={`inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ${compact ? "text-[10px]" : "text-xs"} font-medium ${className}`}
        data-testid={testId || `arrived-chip-${type}-${id}`}
        title={`Arrived at ${new Date(localArrivedAt).toLocaleString("en-GB")}`}
      >
        <CheckCircle size={compact ? 10 : 12} weight="fill" />
        Arrived {formatTime(localArrivedAt)}
      </div>
    );
  }

  const getPosition = () => new Promise((resolve) => {
    if (!("geolocation" in navigator)) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
  });

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setLoading(true);
    try {
      const coords = await getPosition();
      const endpoint = type === "wo"
        ? `/supplier-portal/work-orders/${id}/arrived`
        : `/supplier-portal/tasks/${id}/arrived`;
      const { data } = await API.post(endpoint, coords || {});
      setLocalArrivedAt(data.arrived_at);
      if (data.travel_time_seconds != null && data.travel_time_seconds < 3600) {
        toast.success(`Arrival recorded (${Math.round(data.travel_time_seconds / 60)} min travel time)`);
      } else {
        toast.success("Arrival recorded");
      }
      onArrived?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record arrival");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      size={size}
      variant={variant}
      onClick={handleClick}
      disabled={loading}
      className={`${className} ${variant === "default" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
      data-testid={testId || `mark-arrived-${type}-${id}`}
    >
      {loading
        ? <Spinner size={12} className="animate-spin mr-1" />
        : <MapPin size={compact ? 12 : 14} weight="fill" className="mr-1" />}
      {compact ? "Arrived" : "Mark as arrived"}
    </Button>
  );
}
